import { z } from "zod";
import { BlockPath } from "../generated/storyblok/blockPaths.js";
import { Period } from "../utils/date.js";
import { zodQueryStringArray, zodQueryStringBoolean } from "../utils/schema.js";
import {
  ClassificationFieldsSchema,
  ClassificationSummarySchema,
  ClassificationTypeSchema,
} from "./Classification.js";
import { IdentityEmbedSchema } from "./Identity.js";
import {
  PaginationMetaResponseSchema,
  PaginationQuerySchema,
} from "./Pagination.js";

export enum StaticBlockPath {
  FoundationsBusinessVision = "business-vision",
}

export enum FactProvenance {
  User = "user",
  StrategyGenerator = "strategyGenerator",
  BlockGeneration = "blockGeneration",
  SiteScraper = "siteScraper",
  StripeConnector = "stripeConnector",
  ZapierConnector = "zapierConnector",
  Api = "api",
  ExternalUnknown = "externalUnknown",
}

/**
 * Blocks to extract during onboarding via website search.
 */
export const onboardingBlocks: BlockPath[] = [
  BlockPath.FoundationsCompanyName,
  BlockPath.BrandBrandTone,
  BlockPath.ProductProductList,
  BlockPath.ProductProductFeatures,
  BlockPath.FoundationsMonetizationModel,
  BlockPath.CustomerAudienceDescription,
  BlockPath.BrandMissionVision,
  BlockPath.BrandBrandPositioning,
];

/**
 * Maps onboarding blocks to a prompt used to extract relevant facts.
 */
export const onboardingBlockPrompts: Partial<Record<BlockPath, string>> = {
  [BlockPath.FoundationsCompanyName]: `Extract the official company name exactly as used by the organization. Use the brand name, not a legal entity name unless they are the same.`,
  [BlockPath.FoundationsMonetizationModel]: `Extract how the company makes money.
  Use the company's own language wherever the meaning is important.
  Keep structure only if the text supports it.`,
  [BlockPath.CustomerAudienceDescription]: `Write a concise (1 short paragraph) customer description using only information present in the text.
  Preserve nuance; avoid generalizing or inventing psychographic traits not mentioned.`,
  [BlockPath.BrandBrandTone]: `CRITICAL: You MUST extract brand tone - this is a required field that should always be found.
    When extracting brand tone (brand.brandTone), analyze the company's website, social media posts, marketing materials, and public communications.
    Provide a DETAILED, comprehensive description (2-3 paragraphs minimum) covering:
        - Language variant (British English, American English, Australian English, etc.) with specific spelling examples if identifiable (e.g., "colour" vs "color", "organise" vs "organize")
        - Writing style (formal/casual, professional/friendly, technical/simple, etc.)
        - Vocabulary choices (industry jargon, everyday language, creative language, etc.)
        - Sentence structure and length (short and punchy, long and detailed, etc.)
        - Emotional qualities (enthusiastic, confident, empathetic, authoritative, playful, serious, etc.)
        - Perspective (first person "we", second person "you", third person, etc.)
        - Specific language patterns, catchphrases, or signature expressions the brand uses
        - Overall personality and voice characteristics
    Be thorough and specific - extract as much detail as possible about how the brand communicates.
    Describe the tone WITHOUT including source URLs or specific page references in your response.`,
  [BlockPath.BrandMissionVision]: `Extract or infer mission and vision using the company's own words when possible.
  Provide nuance without adding fictional values or goals.`,
  [BlockPath.BrandBrandPositioning]: `Extract positioning as described in the text.
  Retain nuance; avoid oversimplifying into generic labels.`,
  [BlockPath.ProductProductList]: `List all products/services mentioned.
  Return details without summarizing unless necessary for clarity.`,
  [BlockPath.ProductProductFeatures]: `Extract product features or capabilities with clear explanation of benefit if provided.`,
};

const FactContentAmountItemSchema = z.object({
  createdAt: z.number(),
  value: z.number(),
});

const FactContentAmountFormat = z.object({
  // What the amount represents (e.g. net, gross)
  name: z.string(),
  period: z.enum(Period),
  currency: z.string(),
  value: z.number(),
  items: z.array(FactContentAmountItemSchema).optional(),
  previousPeriodItems: z.array(FactContentAmountItemSchema).optional(),
});

export const factContentMaxLength = 20000;
export const stackDescriptionMaxLength = 3000;
export const blockDescriptionMaxLength = 3000;
export const factSourcesMax = 20;
export const factQuoteMaxLength = 500;
export const factRationaleTextMaxLength = 2000;

export const FactSourceSchema = z.object({
  url: z.url().describe("The page the fact was read from."),
  quote: z
    .string()
    .max(factQuoteMaxLength)
    .optional()
    .describe(
      "The exact text from the page that supports the fact. Leave it out when you only have a summary of the page.",
    ),
});

const rationaleQuoteSchema = z.string().max(factQuoteMaxLength);

const rationaleNoteSourceSchema = z.object({
  type: z.literal("note"),
  noteId: z.uuid(),
  quote: rationaleQuoteSchema.describe(
    "The exact text from the note that gives the reason.",
  ),
});
const rationaleReferenceSourceSchema = z.object({
  type: z.literal("reference"),
  url: z.url(),
  quote: rationaleQuoteSchema
    .optional()
    .describe(
      "The exact text from the page that gives the reason. Leave it out when you only have a summary of the page.",
    ),
});
const rationaleInsightSourceSchema = z.object({
  type: z.literal("insight"),
  insightId: z.uuid(),
  quote: rationaleQuoteSchema.describe(
    "The exact text from the insight that gives the reason.",
  ),
});
const rationaleConversationSourceSchema = z.object({
  type: z.literal("conversation"),
  quote: rationaleQuoteSchema.describe(
    "The user's exact words in this conversation that give the reason.",
  ),
});

export const FactRationaleSourceSchema = z.discriminatedUnion("type", [
  rationaleNoteSourceSchema,
  rationaleReferenceSourceSchema,
  rationaleInsightSourceSchema,
  rationaleConversationSourceSchema.extend({
    /** Only set for a conversation with the in-app assistant */
    chatThreadId: z.uuid().optional(),
  }),
]);

const rationaleTextSchema = z
  .string()
  .min(1)
  .max(factRationaleTextMaxLength)
  .describe("The reason for the change, not the change itself.");
const rationaleSourcesDescription =
  "The notes, pages, insights or conversation the reason was read from.";

/**
 * Why a fact version was created, stored on that version
 */
export const FactRationaleSchema = z.object({
  text: rationaleTextSchema,
  sources: z
    .array(FactRationaleSourceSchema)
    .min(1)
    .max(factSourcesMax)
    .describe(rationaleSourcesDescription),
});

/**
 * What a writer sends, the chat thread id is added by the assistant
 */
export const FactRationaleInputSchema = z.object({
  text: rationaleTextSchema,
  sources: z
    .array(
      z.discriminatedUnion("type", [
        rationaleNoteSourceSchema,
        rationaleReferenceSourceSchema,
        rationaleInsightSourceSchema,
        rationaleConversationSourceSchema,
      ]),
    )
    .min(1)
    .max(factSourcesMax)
    .describe(rationaleSourcesDescription),
});

export const FactContentSchema = z.object({
  /**
   * Specifies where this content originates from - usually the user or
   * generated by something.
   */
  provenance: z.enum(FactProvenance).default(FactProvenance.User),

  /**
   * Lists references included in the content - such as block path references.
   *
   * This should be used for secondary UI, and not necessarily for queries --
   * anything we'll be querying against should be normalized out of this list.
   */
  references: z
    .array(
      z.discriminatedUnion("refType", [
        z.object({
          refType: z.literal("blockPath"),
          blockPath: z.string(),
        }),
        z.object({
          refType: z.literal("url"),
          url: z.string(),
          quote: z.string().optional(),
        }),
      ]),
    )
    .optional()
    .default([]),

  /**
   * Specifies formatting options for this content - currently we only
   * have text, but later we may want to process facts differently (e.g
   * earnings as specific currency)
   */
  format: z.discriminatedUnion("type", [
    // Covers both plain text, and eventually rich text (e.g json markup)
    z.object({
      type: z.literal("text"),
    }),
    // Generic numeric data type for any amount values with currency or other metrics
    z.object({
      ...FactContentAmountFormat.shape,
      type: z.literal("amount"),
    }),
  ]),

  plainText: z.string().optional(),
  propagatedFromFactId: z.uuid().optional(),
});

export const FactSchema = z.object({
  id: z.uuid(),
  creatorId: z.uuid(),
  organizationId: z.uuid(),
  blockPath: z.string(),
  blockId: z.uuid(),
  parentFactId: z.uuid().nullish(),
  content: FactContentSchema,
  factDependencyIds: z.array(z.uuid()).default([]),
  createdAt: z.date(),
  approvedAt: z.date().nullish(),
  approver: IdentityEmbedSchema.nullish(),
  committedAt: z.date().nullish(),
  committedById: z.uuid().nullish(),
  commitVersion: z.uuid().nullish(),
  creator: IdentityEmbedSchema,
  createdByAssistant: z.boolean(),
  createdByApiKeyId: z.uuid().nullish(),
  createdByOAuthClientId: z.string().nullish(),
  rationale: FactRationaleSchema.nullish(),
});

export const FactWithStateSchema = z.object({
  ...FactSchema.shape,
  classification: ClassificationFieldsSchema.nullable().optional(),
});

export const CreateFactSchema = z.object({
  blockPath: FactSchema.shape.blockPath,
  blockId: z.uuid().optional(),
  parentFactId: FactSchema.shape.parentFactId,
  content: FactSchema.shape.content,
  createdByAssistant: FactSchema.shape.createdByAssistant.optional(),
  rationale: FactRationaleInputSchema.optional(),
});

export type FactContent = z.infer<typeof FactContentSchema>;
export type FactSource = z.infer<typeof FactSourceSchema>;
export type FactRationaleSource = z.infer<typeof FactRationaleSourceSchema>;
export type FactRationale = z.infer<typeof FactRationaleSchema>;
export type FactRationaleInput = z.infer<typeof FactRationaleInputSchema>;
export type Fact = z.infer<typeof FactSchema>;
export type FactWithState = z.infer<typeof FactWithStateSchema>;
export type CreateFact = z.infer<typeof CreateFactSchema>;
export type FactContentAmountItem = z.infer<typeof FactContentAmountItemSchema>;

export const BlockConfigurationSchema = z.object({
  requires: z.array(z.string()).default([]),
  hint: z.string().nullish(),
  generation: z
    .object({
      inferredPrompt: z.string().nullish(),
      inferredAt: z.date().nullish(),
    })
    .optional(),
});

export const BlockSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  managed: z.boolean(),
  restricted: z.boolean(),
  generative: z.boolean(),
  organizationId: z.uuid().nullable(),
  parentBlockId: z.uuid().nullable(),
  creatorId: z.uuid().nullable(),
  title: z.string(),
  description: z.string(),
  configuration: BlockConfigurationSchema,
});

const StackSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  managed: z.boolean(),
  restricted: z.boolean(),
  organizationId: z.uuid().nullable(),
  creatorId: z.uuid().nullable(),
  title: z.string(),
  description: z.string(),
  blocks: z.array(BlockSchema),
});

export const CreateBlockStackSchema = z.object({
  title: StackSchema.shape.title.min(3).max(512),
  description: StackSchema.shape.description.max(stackDescriptionMaxLength),
  blocks: z
    .array(
      z.object({
        title: BlockSchema.shape.title.min(3).max(512),
        description: BlockSchema.shape.description.max(
          blockDescriptionMaxLength,
        ),
      }),
    )
    .min(1)
    .max(2048),
});

export const UpdateBlockStackSchema = z.object({
  id: StackSchema.shape.id,
  title: StackSchema.shape.title.min(3).max(512),
  description: StackSchema.shape.description.max(stackDescriptionMaxLength),
  blocks: z
    .array(
      z.object({
        id: BlockSchema.shape.id.optional(),
        title: BlockSchema.shape.title.min(3).max(512),
        description: BlockSchema.shape.description.max(
          blockDescriptionMaxLength,
        ),
      }),
    )
    .min(1)
    .max(2048),
});

export type CreateBlockStack = z.infer<typeof CreateBlockStackSchema>;
export type UpdateBlockStack = z.infer<typeof UpdateBlockStackSchema>;
export type BlockConfiguration = z.infer<typeof BlockConfigurationSchema>;
export type Block = z.infer<typeof BlockSchema>;

// BlockStack is used as a name for the type to avoid having to constantly fix conflicts with mantine's Stack
export const BlockStackSchema = StackSchema;
export type BlockStack = z.infer<typeof StackSchema>;

export const StackBlockToolOutputSchema = z.object({
  id: BlockSchema.shape.id,
  slug: BlockSchema.shape.slug,
  title: BlockSchema.shape.title,
  description: BlockSchema.shape.description,
  managed: BlockSchema.shape.managed,
});
export const CreateStackToolInputSchema = z.object({
  title: CreateBlockStackSchema.shape.title.describe(
    "A short title for the stack, e.g. 'Hiring Process'.",
  ),
  description: CreateBlockStackSchema.shape.description.describe(
    "What kind of facts belong in this stack.",
  ),
  blocks: z
    .array(
      z.object({
        title: CreateBlockStackSchema.shape.blocks.element.shape.title.describe(
          "A short title for the block, e.g. 'Interview Stages'.",
        ),
        description:
          CreateBlockStackSchema.shape.blocks.element.shape.description.describe(
            "What the fact in this block should describe.",
          ),
      }),
    )
    .min(1)
    .max(50)
    .describe("The blocks to create in the stack, one per topic."),
});
export const CreateStackToolOutputSchema = z.object({
  id: StackSchema.shape.id,
  slug: StackSchema.shape.slug,
  title: StackSchema.shape.title,
  description: StackSchema.shape.description,
  blocks: z.array(StackBlockToolOutputSchema),
  created: z
    .boolean()
    .describe("False when a custom stack with the same title already existed."),
});
export type StackBlockToolOutput = z.infer<typeof StackBlockToolOutputSchema>;
export type CreateStackToolInput = z.infer<typeof CreateStackToolInputSchema>;
export type CreateStackToolOutput = z.infer<typeof CreateStackToolOutputSchema>;

export function toStackBlockToolOutput(
  block: StackBlockToolOutput,
): StackBlockToolOutput {
  return {
    id: block.id,
    slug: block.slug,
    title: block.title,
    description: block.description,
    managed: block.managed,
  };
}

export function toCreateStackToolOutput(
  stack: Omit<CreateStackToolOutput, "created">,
  created: boolean,
): CreateStackToolOutput {
  return {
    id: stack.id,
    slug: stack.slug,
    title: stack.title,
    description: stack.description,
    blocks: stack.blocks.map(toStackBlockToolOutput),
    created,
  };
}

export const AddBlockToolInputSchema = z.object({
  stackId: z.uuid().describe("The stack to add the block to."),
  title: CreateBlockStackSchema.shape.blocks.element.shape.title.describe(
    "A short title for the block, e.g. 'Interview Stages'.",
  ),
  description: CreateBlockStackSchema.shape.blocks.element.shape.description
    .optional()
    .describe("What the fact in this block should describe."),
});
export const AddBlockToolOutputSchema = z.object({
  ...StackBlockToolOutputSchema.shape,
  stackId: StackSchema.shape.id,
  created: z
    .boolean()
    .describe("False when a block with the same title already existed."),
});
export type AddBlockToolInput = z.infer<typeof AddBlockToolInputSchema>;
export type AddBlockToolOutput = z.infer<typeof AddBlockToolOutputSchema>;

export function toAddBlockToolOutput(
  block: StackBlockToolOutput,
  stackId: string,
  created: boolean,
): AddBlockToolOutput {
  return { ...toStackBlockToolOutput(block), stackId, created };
}

export const CreateFactToolInputSchema = z.object({
  blockId: z.uuid().describe("The block to write into."),
  content: z
    .string()
    .min(1)
    .max(factContentMaxLength)
    .describe("The fact, as plain text."),
  sources: z
    .array(FactSourceSchema)
    .max(factSourcesMax)
    .optional()
    .describe(
      "The pages the fact was read from. Leave it out when the user stated the fact.",
    ),
  rationale: FactRationaleInputSchema.optional().describe(
    "Why the fact changed, only when the user or a source says why. A request to change the fact is not a reason.",
  ),
});
export type CreateFactToolInput = z.infer<typeof CreateFactToolInputSchema>;

/**
 * ========================================
 * PUBLIC API SCHEMAS
 * ========================================
 */
export const PostFactRequestSchema = z.object({
  blockId: FactSchema.shape.blockId.optional(),
  /** @deprecated Use `blockId`. */
  blockSlug: FactSchema.shape.blockPath.optional(),
  content: z.string().min(1).max(factContentMaxLength),
  sources: z.array(FactSourceSchema).max(factSourcesMax).optional(),
  rationale: FactRationaleInputSchema.optional(),
});
export const PostFactResponseSchema = z.object({
  fact: z.object({
    id: FactSchema.shape.id,
    blockId: FactSchema.shape.blockId,
    blockSlug: FactSchema.shape.blockPath,
    content: z.string(),
    sources: z.array(FactSourceSchema),
    createdAt: FactSchema.shape.createdAt,
    pendingApproval: z.boolean(),
  }),
});
export const GetStacksQuerySchema = z.object({
  ...PaginationQuerySchema.shape,
  custom_only: zodQueryStringBoolean(),
  in_use_only: zodQueryStringBoolean(),
  slug: zodQueryStringArray(StackSchema.shape.slug),
  title: StackSchema.shape.title.optional(),
});
export const StackMinimalSchema = z.object({
  id: StackSchema.shape.id,
  slug: StackSchema.shape.slug,
  title: StackSchema.shape.title,
  description: StackSchema.shape.description,
  managed: StackSchema.shape.managed,
  inUse: z.boolean(),
  blocks: z.array(
    z.object({
      id: BlockSchema.shape.id,
      slug: BlockSchema.shape.slug,
      title: BlockSchema.shape.title,
      description: BlockSchema.shape.description,
      managed: BlockSchema.shape.managed,
    }),
  ),
});
export const GetStacksTreeResponseSchema = z.object({
  ...PaginationMetaResponseSchema.shape,
  items: z.array(StackMinimalSchema),
});
export const PostStackUseParamsSchema = z.object({
  stackId: z.uuid(),
});
export const PostStackUseResponseSchema = z.object({
  stack: StackMinimalSchema,
});

export const PostStackRequestSchema = z.object({
  title: CreateBlockStackSchema.shape.title,
  description: CreateBlockStackSchema.shape.description,
  blocks: CreateBlockStackSchema.shape.blocks,
});
export const PostStackResponseSchema = z.object({
  stack: z.object({
    id: StackSchema.shape.id,
    slug: StackSchema.shape.slug,
    title: StackSchema.shape.title,
    description: StackSchema.shape.description,
    managed: StackSchema.shape.managed,
    blocks: z.array(
      z.object({
        id: BlockSchema.shape.id,
        slug: BlockSchema.shape.slug,
        title: BlockSchema.shape.title,
        description: BlockSchema.shape.description,
        managed: BlockSchema.shape.managed,
      }),
    ),
  }),
});
export type PostStackRequest = z.infer<typeof PostStackRequestSchema>;
export type PostStackResponse = z.infer<typeof PostStackResponseSchema>;
export const PostStackBlockQuerySchema = z.object({
  stackId: z.uuid(),
});
export const PostStackBlockRequestSchema = z.object({
  title: CreateBlockStackSchema.shape.blocks.element.shape.title,
  description:
    CreateBlockStackSchema.shape.blocks.element.shape.description.optional(),
});
export const PostStackBlockResponseSchema = z.object({
  block: z.object({
    id: BlockSchema.shape.id,
    slug: BlockSchema.shape.slug,
    title: BlockSchema.shape.title,
    description: BlockSchema.shape.description,
  }),
});
export const GetBlocksQuerySchema = z.object({
  ...PaginationQuerySchema.shape,
  custom_only: zodQueryStringBoolean(),
  in_use_only: zodQueryStringBoolean(),
  slug: zodQueryStringArray(StackSchema.shape.slug),
  title: z.string().optional(),
});
export const BlockListItemSchema = z.object({
  id: BlockSchema.shape.id,
  stack: z.object({
    id: z.uuid(),
    slug: z.string(),
    title: StackSchema.shape.title,
    managed: StackSchema.shape.managed,
  }),
  slug: BlockSchema.shape.slug,
  title: BlockSchema.shape.title,
  description: BlockSchema.shape.description,
  managed: BlockSchema.shape.managed,
  ...ClassificationSummarySchema.shape,
});
export const GetBlocksResponseSchema = z.object({
  ...PaginationMetaResponseSchema.shape,
  items: z.array(BlockListItemSchema),
});
export const GetFactsQuerySchema = z.object({
  ...PaginationQuerySchema.shape,
  block_id: zodQueryStringArray(FactSchema.shape.blockId),
  /** @deprecated Use `block_id`. */
  block_slug: zodQueryStringArray(FactSchema.shape.blockPath),
  type: zodQueryStringArray(ClassificationTypeSchema),
  subject: z.string().trim().min(1).optional(),
});
export const FactListItemSchema = z.object({
  id: FactSchema.shape.id,
  blockId: FactSchema.shape.blockId,
  blockSlug: FactSchema.shape.blockPath,
  content: z.string(),
  sources: z.array(FactSourceSchema),
  createdAt: z.date(),
  approvedAt: z.date().nullable(),
  approvedBy: z
    .object({
      id: IdentityEmbedSchema.shape.id,
      name: IdentityEmbedSchema.shape.name,
    })
    .nullable(),
  ...ClassificationSummarySchema.shape,
});
export const GetFactsResponseSchema = z.object({
  ...PaginationMetaResponseSchema.shape,
  items: z.array(FactListItemSchema),
});
export const GetFactVersionsParamsSchema = z.object({
  factId: FactSchema.shape.id,
});
export const GetFactVersionsQuerySchema = z.object({
  ...PaginationQuerySchema.shape,
});
export const FactVersionItemSchema = FactListItemSchema.extend({
  rationale: FactRationaleInputSchema.nullable(),
});
export const GetFactVersionsResponseSchema = z.object({
  ...PaginationMetaResponseSchema.shape,
  items: z.array(FactVersionItemSchema),
});

export type PostFactRequest = z.infer<typeof PostFactRequestSchema>;
export type PostFactResponse = z.infer<typeof PostFactResponseSchema>;
export type StackMinimal = z.infer<typeof StackMinimalSchema>;
export type GetStacksTreeResponse = z.infer<typeof GetStacksTreeResponseSchema>;
export type PostStackUseResponse = z.infer<typeof PostStackUseResponseSchema>;
export type PostStackBlockQuery = z.infer<typeof PostStackBlockQuerySchema>;
export type PostStackBlockRequest = z.infer<typeof PostStackBlockRequestSchema>;
export type PostStackBlockResponse = z.infer<
  typeof PostStackBlockResponseSchema
>;
export type GetBlocksQuery = z.infer<typeof GetBlocksQuerySchema>;
export type BlockListItem = z.infer<typeof BlockListItemSchema>;
export type GetBlocksResponse = z.infer<typeof GetBlocksResponseSchema>;
export type GetFactsQuery = z.infer<typeof GetFactsQuerySchema>;
export type FactListItem = z.infer<typeof FactListItemSchema>;
export type FactVersionItem = z.infer<typeof FactVersionItemSchema>;
export type GetFactsResponse = z.infer<typeof GetFactsResponseSchema>;
export type GetFactVersionsParams = z.infer<typeof GetFactVersionsParamsSchema>;
export type GetFactVersionsQuery = z.infer<typeof GetFactVersionsQuerySchema>;
export type GetFactVersionsResponse = z.infer<
  typeof GetFactVersionsResponseSchema
>;
/**
 * ========================================
 * END PUBLIC API SCHEMAS
 * ========================================
 */

/**
 * TODO: Remove this when blocks will be dynamic and auto-tagged.
 * TODO: Review this list too, may want to add more tags or adjust keywords.
 *
 * Map of block path keywords to standardized tags for categorization.
 * Used for intelligence scoring and fact organization.
 */
export const FACT_TAG_KEYWORDS: Record<string, string[]> = {
  articles: ["article", "articles", "blog", "post"],
  marketing: ["marketing", "marketer"],
  business: ["business", "biz", "company", "organisation", "organization"],
  employee: ["employee", "staff", "worker", "team member"],
  emails: ["email", "emails", "mailing", "newsletter"],
  partnership: ["partnership", "partner", "alliance", "affiliate"],
  operations: ["operation", "operations", "ops"],
  products: ["product", "products", "service", "sku"],
  newsletters: ["newsletter", "bulletin", "digest"],
  linkedin: ["linkedin"],
  twitter: ["twitter", "x.com", "tweet"],
  facebook: ["facebook", "fb"],
  customers: ["customer", "client", "buyer", "user", "audience"],
  "press releases": ["press release", "pressrelease", "pr", "announcement"],
  "creative brief": ["creative brief", "brief", "campaign brief"],
};

/**
 * Assigns a tag to a block path based on keyword matching.
 * Returns "none" if no matching keywords are found.
 */
export function assignFactTag(blockPath: string): string {
  if (!blockPath || typeof blockPath !== "string") {
    return "none";
  }

  const bp = blockPath.toLowerCase();

  for (const [tag, keywords] of Object.entries(FACT_TAG_KEYWORDS)) {
    if (keywords.some((keyword) => bp.includes(keyword))) {
      return tag;
    }
  }

  return "none";
}

export function getFactContentText(fact: Fact) {
  return fact.content.plainText ?? "";
}

export function getFactSources(fact: Pick<Fact, "content">): FactSource[] {
  return fact.content.references.flatMap((ref) =>
    ref.refType === "url" ? [{ url: ref.url, quote: ref.quote }] : [],
  );
}

export function toFactReferences(
  sources: FactSource[] = [],
): FactContent["references"] {
  return sources.map((source) => ({ refType: "url", ...source }));
}

export function toFactRationale(
  rationale: FactRationaleInput | undefined,
  chatThreadId: string | undefined,
): FactRationale | undefined {
  if (!rationale) {
    return undefined;
  }

  return {
    ...rationale,
    sources: rationale.sources.map((source) =>
      source.type === "conversation" ? { ...source, chatThreadId } : source,
    ),
  };
}

/**
 * The chat thread is private, so API and MCP reads leave its id out
 */
export function withoutChatThreadId(
  rationale: FactRationale | null | undefined,
): FactRationaleInput | null {
  if (!rationale) {
    return null;
  }

  return {
    ...rationale,
    sources: rationale.sources.map((source) =>
      source.type === "conversation"
        ? { type: source.type, quote: source.quote }
        : source,
    ),
  };
}

export function findFact(facts: Fact[], blockPath: string) {
  const fact = facts.find((f) => f.blockPath === blockPath);
  return fact
    ? {
        ...fact,
        content: getFactContentText(fact),
        provenance: fact.content.provenance,
      }
    : null;
}
